import { useCallback, useEffect, lazy, Suspense, useRef } from 'react'
import { QuickView } from 'client/components/common/QuickView'
import { useDispatch, useSelector } from 'react-redux'
import { getQuickViewId } from '~/client/store/debug'
import { resetQuickViewId, setIsSimilarProduct } from '~/client/store/debug/actions'
import { getIsDirectQuickView, getSimilarProduct, getTakeOverQV } from 'client/store/debug/reducer'
import { GalleryQuickViewSpinner } from '~/client/components/Gallery/PreviewArea/GalleryQuickView/GalleryQuickViewSpinner'
import { resetSimilarTemplates } from '~/client/store/similarTemplates/action'
import { PageId, CouponCalloutProvider } from '@vp/coupon-callout'
import config from 'config'
import { getLocaleSelector } from '~/client/store/config'
import { ThunkDispatch } from '~/client/store/interface'
import { useValidQuickViewEntity } from 'client/components/Gallery/PreviewArea/GalleryQuickView/hooks/useValidQuickViewEntity'
import { trackDirectQVDismissImpression } from '~/client/store/analytics/thunks'
import { useAnalytics } from '~/client/hooks/gallery/useAnalytics'
import { similarProductsReset } from '~/client/store/similarProducts/action'

const GalleryQuickView = lazy(() => import('client/components/Gallery/PreviewArea/GalleryQuickView'))

export const QuickViewModal = () => {
  const dispatch = useDispatch<ThunkDispatch>()
  const isTakeOverQV = useSelector(getTakeOverQV)
  const designId = useSelector(getQuickViewId)
  const locale = useSelector(getLocaleSelector)
  const isDirectQuickView = useSelector(getIsDirectQuickView)
  const isSimilarProduct = !!useSelector(getSimilarProduct)
  const analytics = useAnalytics()

  const tileEntity = useValidQuickViewEntity(designId)

  const handleOnRequestDismiss = useCallback((): void => {
    if (isTakeOverQV) {
      // go back one step in the history stack
      window.history.back()
      return
    }

    const shouldTriggerImpression = isDirectQuickView && designId
    if (shouldTriggerImpression) {
      dispatch(trackDirectQVDismissImpression(designId, analytics))
    }

    // Wrapped in setTimeout in order to improve INP performance
    setTimeout(() => {
      if (isSimilarProduct) {
        dispatch(setIsSimilarProduct(false))
      }
      dispatch(resetQuickViewId(designId))
      dispatch(resetSimilarTemplates())
      dispatch(similarProductsReset())
    })
  }, [isTakeOverQV, isDirectQuickView, dispatch, designId, analytics])

  const modalRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    modalRef.current?.scrollTo(0, 0)
  }, [designId])

  return (
    <QuickView
      onlyRenderWhenOpen
      designId={designId}
      id={`quickview-${designId}`}
      isOpen={!!tileEntity}
      ref={modalRef}
      onRequestDismiss={handleOnRequestDismiss}
    >
      <Suspense fallback={<GalleryQuickViewSpinner />}>
        <CouponCalloutProvider baseConfig={{ tenant: config.tenant, pageId: PageId.Gallery, locale }}>
          {tileEntity && <GalleryQuickView tileEntity={tileEntity} />}
        </CouponCalloutProvider>
      </Suspense>
    </QuickView>
  )
}
